import { Stack, Typography, IconButton, Button, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { TFile } from 'types/user';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileInboxNotesTableCb } from './FileInboxNotesTable';
import { StorageServices } from 'services/storage';
import { ROLE } from 'const/common';

const storage = new StorageServices();

type Props = {
  isMobile: boolean;
  file: TFile;
  selectedFile?: TFile | null;
  selected?: boolean;
  onClick?: (value: { isUnread?: boolean; onReply?: boolean }) => void;
  onDeleteByFileId: (fileId: number) => void;
  onDeleteDetail: (idChat: number, fileId: number) => void;
  onSendMessage: (message: string, fileId: string) => void;
};

export const FileInboxItem = ({
  isMobile,
  selectedFile,
  file,
  selected,
  onClick,
  onDeleteByFileId,
  onDeleteDetail,
  onSendMessage
}: Props) => {
  const navigate = useNavigate();
  const lastNote = file.communicationNotes?.[0];
  const [input, setInput] = useState('');
  const [reply, setReply] = useState<boolean>(false);
  const role = storage.getRole();
  const isAdmin = role === ROLE.ADMIN || role === ROLE.ROOT_ADMIN;

  const isUnread = isAdmin
    ? lastNote
      ? !lastNote.isAdminRead
      : false
    : lastNote?.communicationNoteReads?.length
      ? !!lastNote?.communicationNoteReads.find((item) => !item.readAt)
      : false;
  const handleSend = () => {
    if (!input.trim() || !file?.id) return;
    onSendMessage(input, file?.id);
    setInput('');
    setReply(false);
  };
  return (
    <Stack
      position="relative"
      border="1px solid #E0E0E0"
      borderRadius={2}
      p={2}
      sx={{
        cursor: 'pointer',
        backgroundColor: selected ? '#F5F8FF' : 'transparent',
        borderColor: selected ? '#1976d2' : '#E0E0E0'
      }}
      onClick={() => {
        onClick && onClick({ isUnread });
      }}>
      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="start">
        <Stack gap={1} sx={{ minWidth: 0, flex: 1, pr: 1 }}>
          <Stack sx={{ width: '100%' }} alignItems="start">
            <Typography
              onClick={() => {
                if (isMobile) {
                  navigate(`/file/${file.id}`);
                } else {
                  navigate(`/admin/files/${file.id}`);
                }
              }}
              style={{
                textDecoration: 'underline',
                textUnderlineOffset: '1px',
                cursor: 'pointer',
                wordBreak: 'break-word'
              }}
              fontWeight={600}>
              {file?.clientOption?.value} – {file.fileNumber}
            </Typography>
          </Stack>

          <Typography
            fontSize={13}
            color="text.secondary"
            sx={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
            {lastNote?.content ?? 'No message'}
          </Typography>
        </Stack>

        <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
          <Stack flexDirection={'row'} gap={'10px'} alignItems={'center'}>
            <Typography fontSize={12}>
              {lastNote ? dayjs(lastNote.createdAt).format('DD-MM HH:mm') : ''}
            </Typography>
            {isUnread && <Stack width={12} height={12} borderRadius="50%" bgcolor="#E53935" />}
          </Stack>

          <Stack flexDirection={'row'}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setReply(true);
                onClick && onClick({ onReply: true });
              }}>
              <ReplyOutlinedIcon sx={{ cursor: 'pointer', color: '#1976d2' }} />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onDeleteByFileId(Number(file.id));
              }}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
      {selectedFile && selected && (
        <Stack
          style={{ height: 400, overflowY: 'auto' }}
          sx={{ display: { xs: 'flex', md: 'none' } }}
          onClick={(e) => e.stopPropagation()}>
          <Stack mt={2}>
            <FileInboxNotesTableCb
              onDeleteDetail={(idChat) => {
                onDeleteDetail(idChat, Number(file.id));
              }}
              notes={selectedFile.communicationNotes}
            />
          </Stack>
        </Stack>
      )}

      {/* EXPAND */}
      {reply && (
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: '#E3F2FD',
              border: '1px solid #90CAF9',
              borderRadius: 1,
              p: 1,
              mt: 1
            }}>
            <Typography variant="body2" color="text.primary">
              Replying to {file?.clientOption?.value} - {file.fileNumber}
            </Typography>
            <Button size="small" onClick={() => setReply(false)}>
              Cancel
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} pt={1} onClick={(e) => e.stopPropagation()}>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter note..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSend();
              }}
            />
            <Button variant="contained" onClick={handleSend}>
              Send
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
};
